
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface RecentAlert {
  id: string;
  type: "warning" | "danger" | "info";
  title: string;
  description: string;
  severity: "high" | "medium" | "low";
  source?: "internal_message" | "time_entry" | "absence";
  sourceId?: string;
}

export const useRecentAlerts = () => {
  const [alerts, setAlerts] = useState<RecentAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentAlerts = async () => {
      try {
        setLoading(true);
        console.log("🔍 [RecentAlerts] Début de la récupération des alertes récentes");
        
        const recentAlerts: RecentAlert[] = [];
        let alertId = 1;

        // 1. Récupérer les messages internes pour les afficher comme alertes
        console.log("📨 [RecentAlerts] Récupération des messages internes...");
        const { data: internalMessages, error: messagesError } = await supabase
          .from('internal_messages')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(5);

        if (!messagesError && internalMessages) {
          console.log(`📨 [RecentAlerts] ${internalMessages.length} messages internes trouvés`);
          
          internalMessages.forEach(message => {
            const alertType = message.priority === 'urgent' ? 'danger' : 
                            message.priority === 'high' ? 'warning' : 'info';
            const severity = message.priority === 'urgent' || message.priority === 'high' ? 'high' : 'medium';
            
            recentAlerts.push({
              id: `message-${alertId++}`,
              type: alertType,
              title: `Signalement: ${message.subject}`,
              description: `De: ${message.sender_name}`,
              severity,
              source: "internal_message",
              sourceId: message.id
            });
          });
        } else if (messagesError) {
          console.error("❌ [RecentAlerts] Erreur messages internes:", messagesError);
        }

        // 2. Récupérer les entrées de temps pour analyser les anomalies
        console.log("⏰ [RecentAlerts] Récupération des entrées de temps...");
        const { data: timeEntries, error } = await supabase
          .from('time_entries')
          .select(`
            *,
            profiles!inner(name),
            worksites!inner(name)
          `)
          .gte('date', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
          .order('date', { ascending: false });

        if (error) {
          console.error("❌ [RecentAlerts] Erreur time_entries:", error);
          throw error;
        }

        console.log(`⏰ [RecentAlerts] ${timeEntries?.length || 0} entrées de temps trouvées`);

        // 3. Analyser les entrées pour générer des alertes
        timeEntries?.forEach(entry => {
          if (entry.start_time && entry.end_time) {
            const startTime = new Date(`1970-01-01T${entry.start_time}`);
            const endTime = new Date(`1970-01-01T${entry.end_time}`);
            const heures = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

            // Alerte pour plus de 10h de travail
            if (heures > 10) {
              recentAlerts.push({
                id: `overtime-${alertId++}`,
                type: "warning",
                title: "Heures supplémentaires excessives",
                description: `${entry.profiles.name} - ${Math.round(heures * 10) / 10}h le ${new Date(entry.date).toLocaleDateString()}`,
                severity: "high",
                source: "time_entry",
                sourceId: entry.id
              });
            }
          }

          // Alerte pour entrée sans sortie
          if (entry.start_time && !entry.end_time) {
            recentAlerts.push({
              id: `missing-exit-${alertId++}`,
              type: "danger",
              title: "Pointage de sortie manquant",
              description: `${entry.profiles.name} - ${entry.worksites.name}`,
              severity: "high",
              source: "time_entry",
              sourceId: entry.id
            });
          }
        });

        // 4. Vérifier les absences (profils actifs sans entrées récentes)
        console.log("👥 [RecentAlerts] Vérification des absences...");
        const { data: activeProfiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name')
          .eq('active', true)
          .eq('role', 'ouvrier');

        if (!profilesError && activeProfiles) {
          const workersWithEntries = new Set(timeEntries?.map(entry => entry.profiles.name) || []);
          
          activeProfiles.forEach(profile => {
            if (!workersWithEntries.has(profile.name)) {
              recentAlerts.push({
                id: `absence-${alertId++}`,
                type: "info",
                title: "Aucune activité récente",
                description: `${profile.name}`,
                severity: "medium",
                source: "absence",
                sourceId: profile.id
              });
            }
          });
        }

        // Trier par ordre d'importance et prendre les 3 plus importantes
        const sortedAlerts = recentAlerts
          .sort((a, b) => {
            const severityOrder = { high: 3, medium: 2, low: 1 };
            return severityOrder[b.severity] - severityOrder[a.severity];
          })
          .slice(0, 3);

        console.log(`✅ [RecentAlerts] ${sortedAlerts.length} alertes récentes générées`);
        setAlerts(sortedAlerts);

      } catch (error) {
        console.error('❌ [RecentAlerts] Erreur lors du chargement des alertes récentes:', error);
        toast.error('Erreur lors du chargement des alertes récentes');
      } finally {
        setLoading(false);
      }
    };

    fetchRecentAlerts();

    // Configurer un abonnement en temps réel pour les nouveaux messages
    const channel = supabase
      .channel('recent-alerts-updates')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'internal_messages'
      }, () => {
        console.log("📨 [RecentAlerts] Nouveau message détecté, rechargement des alertes");
        fetchRecentAlerts();
      })
      .on('postgres_changes', {
        event: 'DELETE',
        schema: 'public',
        table: 'internal_messages'
      }, () => {
        console.log("🗑️ [RecentAlerts] Message supprimé détecté, rechargement des alertes");
        fetchRecentAlerts();
      })
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'time_entries'
      }, () => {
        console.log("⏰ [RecentAlerts] Nouvelle entrée de temps détectée, rechargement des alertes");
        fetchRecentAlerts();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return { alerts, loading };
};
